import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Schedule } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Course, CourseOwner, Mentor, Statistics } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const Home: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [featuredCourses, setFeaturedCourses] = useState<Course[]>([]);
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [owners, setOwners] = useState<CourseOwner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [stats, featured, featuredMentors, ownersList] = await Promise.all([
          apiService.getDashboardStats(),
          apiService.getFeaturedCourses(),
          apiService.getFeaturedMentors(),
          apiService.getCourseOwners(),
        ]);
        
        setStatistics(stats);
        setFeaturedCourses(featured);
        setMentors(featuredMentors);
        setOwners(ownersList);
      } catch (error) {
        console.error('Error fetching data:', error);
        try {
          const [featured, featuredMentors, ownersList] = await Promise.all([
            apiService.getFeaturedCourses(),
            apiService.getFeaturedMentors(),
            apiService.getCourseOwners().catch(() => []),
          ]);
          
          setFeaturedCourses(featured);
          setMentors(featuredMentors);
          setOwners(ownersList);
          
          // Use fallback statistics only if API completely fails
          setStatistics({
            total_students: featured.length * 50, // Estimate based on courses
            total_courses: featured.length,
            total_categories: 6,
            total_mentors: featuredMentors.length,
            total_purchases: featured.length * 25, // Estimate
            total_revenue: featured.reduce((sum: number, course: Course) => sum + course.price, 0) * 20, // Estimate
            active_subscriptions: featured.length * 20, // Estimate
            expired_subscriptions: featured.length * 5, // Estimate
            subscriptions_expiring_soon: featured.length * 2, // Estimate
            total_videos_completed: featured.length * 100, // Estimate
            average_progress_per_course: 65.5, // Estimate
          });
        } catch (fallbackError) {
          console.error('Fallback data fetch failed:', fallbackError);
          // Final fallback
          setStatistics({
            total_students: 1250,
            total_courses: 45,
            total_categories: 8,
            total_mentors: 12,
            total_purchases: 3200,
            total_revenue: 125000,
            active_subscriptions: 2800,
            expired_subscriptions: 400,
            subscriptions_expiring_soon: 150,
            total_videos_completed: 4500,
            average_progress_per_course: 72.3,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: 'white',
          py: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            alignItems: 'center', 
            gap: { xs: 2, sm: 3, md: 4, lg: 6 },
            minHeight: { xs: 'auto', md: '350px' },
            py: { xs: 1, sm: 2, md: 2 }
          }}>
            {/* Left Side - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              style={{ flex: 1, width: '100%' }}
            >
              <Typography
                variant={isMobile ? 'h3' : 'h1'}
                component="h1"
                gutterBottom
                sx={{ 
                  fontWeight: 800,
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                  background: 'linear-gradient(45deg, #10b981 30%, #22c55e 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                Creating Opportunities for Everyone
              </Typography>
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                sx={{ 
                  mb: { xs: 2, sm: 3 }, 
                  opacity: 0.9, 
                  fontWeight: 400,
                  lineHeight: 1.5,
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                Learn new skills, grow with confidence, and build a better future through practical learning.
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                gap: 2,
                justifyContent: { xs: 'center', md: 'flex-start' }
              }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    background: 'linear-gradient(45deg, #10b981 30%, #22c55e 90%)',
                    color: '#ffffff',
                    px: { xs: 4, sm: 6 },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 600,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #059669 30%, #16a34a 90%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => navigate('/courses')}
                >
                  Start Learning Today
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: '#10b981',
                    color: '#10b981',
                    px: { xs: 4, sm: 6 },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 600,
                    borderRadius: 3,
                    '&:hover': {
                      borderColor: '#22c55e',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => navigate('/courses')}
                >
                  Browse Courses
                </Button>
              </Box>
            </motion.div>

            {/* Right Side - People with Books Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ flex: 1, display: 'flex', justifyContent: 'center', width: '100%' }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: '200px', sm: '250px', md: '300px', lg: '350px' },
                  height: { xs: '200px', sm: '250px', md: '300px', lg: '350px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Green Neon Glow Effect */}
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, rgba(34, 197, 94, 0.1) 50%, transparent 70%)',
                    filter: 'blur(20px)',
                    animation: 'pulse 2s ease-in-out infinite alternate',
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        opacity: 0.7,
                      },
                      '100%': {
                        transform: 'scale(1.1)',
                        opacity: 1,
                      },
                    },
                  }}
                />
                
                {/* Main Image Container */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '80%',
                    height: '80%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                  }}
                >
                  <Box
                    component="img"
                    src="/3d art.png"
                    alt="People holding books and learning"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 0 20px rgba(16, 185, 129, 0.5))',
                      borderRadius: 2,
                    }}
                  />
                </Box>
              </Box>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Statistics Section */}
      {statistics && (
        <Box sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
              {[
                { label: 'Students', value: statistics.total_students, color: '#10b981' },
                { label: 'Courses', value: statistics.total_courses, color: '#22c55e' },
                { label: 'Categories', value: statistics.total_categories, color: '#34d399' },
                { label: 'Mentors', value: statistics.total_mentors, color: '#4ade80' },
              ].map((stat, index) => (
                <Box key={stat.label} sx={{ textAlign: 'center', flex: '1 1 200px', minWidth: '150px' }}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        boxShadow: `0 8px 32px ${stat.color}40`,
                      }}
                    >
                      <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {stat.label}
                    </Typography>
                  </motion.div>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {/* Featured Courses Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Featured Courses
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Carefully selected courses to help you build real skills and move your career forward.
            </Typography>
          </Box>
          
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            {featuredCourses.length > 0 ? featuredCourses.map((course, index) => (
              <Box
                key={course.id}
                sx={{
                  flex: '0 0 100%',
                  [theme.breakpoints.up('sm')]: { flex: '0 0 calc((100% - 24px) / 2)' },
                  [theme.breakpoints.up('lg')]: { flex: '0 0 calc((100% - 72px) / 4)' },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  style={{ height: '100%' }}
                >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: course.coming_soon ? 'default' : 'pointer',
                    transition: 'all 0.3s ease',
                    border: course.coming_soon 
                      ? '2px solid rgba(251, 191, 36, 0.5)'
                      : '1px solid rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    ...(!course.coming_soon && {
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
                      },
                    }),
                  }}
                  onClick={() => !course.coming_soon && navigate(`/courses/${course.hashed}`)}
                >
                  {/* Card Image */}
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={course.image}
                      alt={course.name}
                      sx={{ 
                        objectFit: 'cover',
                        filter: course.coming_soon ? 'grayscale(30%) brightness(0.9)' : 'none',
                      }}
                    />
                      
                      {/* Coming Soon Overlay */}
                      {course.coming_soon && (
                        <>
                          {/* Semi-transparent overlay */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%)',
                              backdropFilter: 'blur(1px)',
                            }}
                          />
                          
                          {/* Coming Soon Badge - Top Right */}
                          <Chip
                            icon={<Schedule sx={{ fontSize: '0.9rem' }} />}
                            label="COMING SOON"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: '0.65rem',
                              letterSpacing: '0.5px',
                              boxShadow: '0 4px 20px rgba(251, 191, 36, 0.4)',
                              animation: 'pulse 2s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': {
                                  boxShadow: '0 4px 20px rgba(251, 191, 36, 0.4)',
                                },
                                '50%': {
                                  boxShadow: '0 4px 30px rgba(251, 191, 36, 0.8)',
                                },
                              },
                            }}
                          />
                        </>
                      )}
                    </Box>
                    
                    {/* Card Content */}
                    <CardContent sx={{ flexGrow: 1, p: 1.5, pt: 0.75, pb: '0 !important' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                        <Typography 
                          variant="body2" 
                          component="h3" 
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.85rem', 
                            lineHeight: 1.2, 
                            flex: 1,
                            pr: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            opacity: course.coming_soon ? 0.9 : 1,
                          }}
                        >
                          {course.name}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 700, 
                            color: course.coming_soon ? '#fbbf24' : '#10b981', 
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}
                        >
                          ${course.price}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Box>
            )) : (
              <Box sx={{ textAlign: 'center', py: 6, width: '100%' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No featured courses right now
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Browse all courses to find something to learn.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/courses')}
                  sx={{
                    backgroundColor: '#10b981',
                    '&:hover': { backgroundColor: '#059669' },
                  }}
                >
                  Browse Courses
                </Button>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* Mentors Section */}
      <Box sx={{ py: 8, backgroundColor: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Meet Our Expert Mentors
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Learn from mentors who’ve been there and want to help you grow.
            </Typography>
          </Box>
          
          <Box
            sx={{
              display: 'flex',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              justifyContent: 'center',
              gap: 3,
            }}
          >
            {mentors.length > 0 ? mentors.map((mentor, index) => (
              <Box
                key={mentor.id}
                sx={{
                  flex: '0 0 100%',
                  [theme.breakpoints.up('sm')]: { flex: '0 0 calc((100% - 24px) / 2)' },
                  [theme.breakpoints.up('md')]: { flex: '0 0 calc((100% - 72px) / 4)' },
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  style={{ height: '100%' }}
                >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
                    },
                  }}
                  onClick={() => navigate('/mentors')}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={mentor.image}
                      alt={mentor.name}
                      sx={{
                        width: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top', // keep head fully visible, crop from bottom
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 1.5, pt: 0.75, pb: '0 !important' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                      <Typography
                        variant="body2"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          lineHeight: 1.2,
                          flex: 1,
                          pr: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {mentor.name}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
                </motion.div>
              </Box>
            )) : (
              <Box sx={{ textAlign: 'center', py: 8, width: '100%' }}>
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No mentors available at the moment
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Our expert mentors are being prepared. Check back soon!
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* Partnerships / Our Partners */}
      {owners.length > 0 && (
        <Box sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Our Partners
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {owners.map((owner, index) => (
                <motion.div
                  key={owner.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <Box
                    component="img"
                    src={owner.image || undefined}
                    alt={owner.naran}
                    sx={{
                      height: { xs: 48, sm: 56, md: 64 },
                      width: 'auto',
                      maxWidth: 160,
                      objectFit: 'contain',
                      filter: 'grayscale(20%)',
                      opacity: 0.9,
                      '&:hover': {
                        filter: 'grayscale(0%)',
                        opacity: 1,
                      },
                      transition: 'all 0.3s ease',
                    }}
                  />
                </motion.div>
              ))}
            </Box>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default Home;